import React, { useRef, useState, useLayoutEffect } from 'react';
import {
  MenuVariant,
  MenuSize,
  type MenuProps,
  type MenuItemProps,
  type MenuSizeConfig,
} from './types';
import { useMenu, useMenuItem } from './useMenu';
import { Tooltip } from '@/shared/components/Tooltip';

// ============================================================================
// Icons
// ============================================================================

// Chevron right icon for submenu indicator
const ChevronRightIcon: React.FC<{
  size: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ size, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

// Search icon for filtering (leading icon in search bar – per Figma)
const SearchIcon: React.FC<{
  size: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ size, className, style, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    {...rest}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// Clear (X) icon inside search bar – per Figma, circle with X to clear search
const ClearSearchIcon: React.FC<{
  size: number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ size, className, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

// ============================================================================
// MenuItem Component
// ============================================================================

interface MenuItemComponentProps extends MenuItemProps {
  variant: MenuVariant;
  size: MenuSize;
  config: MenuSizeConfig;
  showLeadingIcon: boolean;
  showTrailingArrow: boolean;
  isSelected: boolean;
  isSubmenuOpen: boolean;
  expandOnClick: boolean;
  onSelect: (id: string) => void;
  onToggleSubmenu: (id: string) => void;
  onCloseAllSubmenus: () => void;
  showCheckbox?: boolean | undefined;
  toolTip?: boolean | undefined;
  containerRef?: { current: HTMLElement | null };
}

const MenuItem: React.FC<MenuItemComponentProps> = ({
  id,
  label,
  leadingIcon,
  hasSubmenu = false,
  trailingIcon,
  disabled = false,
  disabledTooltip,
  disabledTooltipSide,
  variant,
  size,
  config,
  showLeadingIcon,
  showTrailingArrow,
  isSelected,
  isSubmenuOpen,
  expandOnClick,
  onSelect,
  onToggleSubmenu,
  onCloseAllSubmenus,
  submenu,
  className = '',
  textColor,
  iconColor,
  showCheckbox,
  toolTip = true,
  fontWeight,
  containerRef: _containerRef,
}) => {
  const {
    hasSubmenuItems,
    styles,
    actualShowSubmenu,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
  } = useMenuItem({
    id,
    disabled,
    variant,
    isSelected,
    isSubmenuOpen,
    expandOnClick,
    submenu,
    onSelect,
    onToggleSubmenu,
  });

  const labelSpanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = labelSpanRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [label]);

  // Determine trailing content
  const renderTrailing = () => {
    if (trailingIcon) {
      return (
        <span
          className="shrink-0"
          style={{ color: iconColor || styles.iconColor }}
        >
          {trailingIcon}
        </span>
      );
    }

    if ((hasSubmenu || hasSubmenuItems) && showTrailingArrow) {
      return (
        <ChevronRightIcon
          size={config.iconSize}
          className={`shrink-0 transition-transform duration-200 ${
            actualShowSubmenu ? 'rotate-90' : ''
          }`}
          style={{ color: iconColor || styles.iconColor }}
        />
      );
    }

    return null;
  };

  const menuItemDiv = (
    <div
      className={`
          flex items-start justify-between
          transition-colors duration-150
          ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          ${className}
        `}
      style={{
        minHeight: config.itemHeight,
        paddingLeft: config.paddingX,
        paddingRight: config.paddingX,
        paddingTop: config.paddingY,
        paddingBottom: config.paddingY,
        backgroundColor: styles.bgColor,
        borderRadius: config.borderRadius,
        color: textColor || styles.textColor,
        fontSize: config.fontSize,
        lineHeight: `${config.lineHeight}px`,
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="menuitem"
      aria-disabled={disabled}
      aria-expanded={hasSubmenuItems ? actualShowSubmenu : undefined}
      aria-haspopup={hasSubmenuItems ? 'menu' : undefined}
      tabIndex={disabled ? -1 : 0}
      data-testid={`menu-item-${id}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e as unknown as React.MouseEvent);
        }
      }}
    >
      {/* Leading content – min-w-0 so long labels can truncate with ellipsis */}
      <div
        className={`flex items-center min-w-0 flex-1 ${!toolTip ? 'overflow-x-hidden' : 'overflow-hidden'}`}
        style={{ gap: config.gap }}
      >
        {showCheckbox && (
          <div
            className={`
                shrink-0 flex items-center justify-center
                w-4 h-4 border rounded transition-colors duration-150
                ${isSelected ? 'bg-primary-color-40 border-primary-color-40' : 'bg-white border-neutral-60 hover:border-primary-color-40'}
              `}
            style={{
              borderColor: isSelected ? '#2a4455' : '#DCDDE0',
              backgroundColor: isSelected ? '#2a4455' : 'transparent',
            }}
          >
            {isSelected && (
              <svg
                width="10"
                height="8"
                viewBox="0 0 10 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        )}
        {showLeadingIcon && leadingIcon != null && (
          <span
            className="shrink-0"
            style={{ color: iconColor || styles.iconColor }}
          >
            {leadingIcon}
          </span>
        )}
        <span
          className={`block min-w-0 ${(disabled && disabledTooltip) || !toolTip ? 'whitespace-normal break-words' : 'whitespace-nowrap'}`}
          style={{
            ...(fontWeight ? { fontWeight } : {}),
          }}
        >
          {disabled && disabledTooltip ? (
            <Tooltip
              content={disabledTooltip}
              delay={300}
              side={disabledTooltipSide ?? 'left'}
              cursor="not-allowed"
              contentMaxWidth={500}
              useContentWidth
              multiline
              forceShow
            >
              <span className="block break-words">{label}</span>
            </Tooltip>
          ) : toolTip && isTruncated ? (
            <Tooltip content={label} contentMaxWidth={400} cursor="inherit">
              <span ref={labelSpanRef} className="block">
                {label}
              </span>
            </Tooltip>
          ) : (
            <span ref={labelSpanRef} className="block">
              {label}
            </span>
          )}
        </span>
      </div>

      {/* Trailing content */}
      {renderTrailing()}
    </div>
  );

  // Disabled tooltip now wraps only the label text within menuItemDiv
  const wrappedContent = menuItemDiv;

  return (
    <div className="relative z-10">
      {wrappedContent}

      {/* Submenu */}
      {actualShowSubmenu && hasSubmenuItems && submenu && (
        <div
          className="absolute z-50"
          style={{
            left: '100%',
            top: 0,
            marginLeft: 4,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Menu
            items={submenu}
            variant={variant}
            size={size}
            showLeadingIcons={showLeadingIcon}
            showTrailingArrows={showTrailingArrow}
            expandOnClick={expandOnClick}
            toolTip={toolTip}
            onItemClick={(itemId) => {
              onSelect(itemId);
              onCloseAllSubmenus();
            }}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Menu Component
// ============================================================================

interface ExtendedMenuProps extends MenuProps {
  expandOnClick?: boolean;
}

const Menu: React.FC<ExtendedMenuProps> = ({
  items,
  variant = MenuVariant.Light,
  size = MenuSize.Medium,
  width = 'fit-content',
  showLeadingIcons = true,
  showTrailingArrows = true,
  expandOnClick = true,
  onItemClick,
  selectedId,
  className = '',
  showSearch = false,
  searchPlaceholder = 'Szukaj',
  showCheckboxes = false,
  multiSelect = false,
  toolTip = true,
  onScrollEnd,
  onSearchChange,
  maxVisibleItems,
  maxHeight,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');

  const {
    config,
    menuRef,
    openSubmenuId,
    containerBg,
    borderColor,
    handleItemClick,
    handleToggleSubmenu,
    handleCloseAllSubmenus,
  } = useMenu({
    variant,
    size,
    expandOnClick,
    onItemClick,
  });

  const filteredItems = React.useMemo(() => {
    if (!showSearch || !searchQuery) {
      return items;
    }
    // When onSearchChange is provided the parent handles filtering server-side.
    // Applying a local filter here would race against the debounce window and
    // show stale "no results" before the API response arrives.
    if (onSearchChange) {
      return items;
    }
    return items.filter((item) => {
      // Don't filter out loading or no-results items
      if (
        item.id === '__loading__' ||
        item.id === '__no_results__' ||
        item.id === '__empty__' ||
        item.id === '__error__'
      ) {
        return true;
      }
      return item.label.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [items, showSearch, searchQuery, onSearchChange]);

  return (
    <div
      ref={menuRef}
      className={`
        rounded-lg shadow-lg flex flex-col overflow-hidden
        ${className}
      `}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        backgroundColor: containerBg,
        border: `1px solid ${borderColor}`,
        padding: 4,
        fontFamily: 'Roboto, sans-serif',
        position: 'relative',
        zIndex: 50,
        ...(maxHeight !== undefined ? { maxHeight } : {}),
      }}
      role="menu"
    >
      {showSearch && (
        <div className="px-1 py-1 bg-inherit z-10 mb-1">
          <div
            className="flex items-center gap-2 px-3 py-2 bg-[#E5E7E9] rounded-md border-2 border-[#2a4455]"
            style={{ height: config.itemHeight }}
          >
            <SearchIcon
              size={config.iconSize}
              className="text-[#6a7278] shrink-0 cursor-pointer"
              aria-hidden
            />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => {
                const v = e.target.value
                  .trimStart()
                  .replace(/\p{Extended_Pictographic}/gu, '');
                setSearchQuery(v);
                onSearchChange?.(v);
              }}
              maxLength={200}
              className="flex-1 min-w-0 bg-transparent outline-none text-base text-[#2a4455] placeholder-[#6a7278]"
              autoFocus
              data-testid="dropdown-search"
              aria-label={searchPlaceholder}
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  onSearchChange?.('');
                }}
                className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full text-[#6a7278] hover:bg-[#2a4455]/10 hover:text-[#2a4455] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a4455] cursor-pointer"
                aria-label="Wyczyść wyszukiwanie"
                data-testid="dropdown-search-clear"
              >
                <ClearSearchIcon size={14} />
              </button>
            )}
          </div>
        </div>
      )}
      <div
        className={`flex-1 overscroll-contain ${maxHeight !== undefined || maxVisibleItems !== undefined ? 'overflow-y-auto' : 'overflow-visible'}`}
        style={
          maxVisibleItems !== undefined
            ? { maxHeight: maxVisibleItems * config.itemHeight }
            : undefined
        }
        onScroll={(e) => {
          const target = e.currentTarget;
          if (
            target.scrollHeight - target.scrollTop <=
            target.clientHeight + 10
          ) {
            onScrollEnd?.();
          }
        }}
      >
        {showSearch &&
        searchQuery &&
        filteredItems.length === 0 &&
        !onSearchChange ? (
          <div
            className="px-3 py-2 text-neutral-60"
            style={{
              fontSize: 14,
              fontFamily: 'Roboto',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              wordWrap: 'break-word',
            }}
            role="status"
            data-testid="menu-no-results"
          >
            Nie znaleziono wyników.
          </div>
        ) : filteredItems.length === 1 &&
          filteredItems[0]?.id === '__loading__' ? (
          <div
            className="flex flex-col items-center justify-center py-6"
            role="status"
          >
            <svg
              className="animate-spin mb-2"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                strokeOpacity="0.2"
              />
              <path
                d="M12 2C6.48 2 2 6.48 2 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <div
              style={{
                color: 'var(--Schemes-On-Surface, #181A1B)',
                fontSize: 14,
                fontFamily: 'Roboto',
                fontWeight: 400,
                lineHeight: '20px',
              }}
            >
              {filteredItems[0]?.label}
            </div>
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <MenuItem
                {...item}
                variant={variant}
                size={size}
                config={config}
                showLeadingIcon={showLeadingIcons}
                showTrailingArrow={showTrailingArrows}
                isSelected={
                  multiSelect && Array.isArray(selectedId)
                    ? selectedId.includes(item.id)
                    : selectedId === item.id || item.selected === true
                }
                isSubmenuOpen={openSubmenuId === item.id}
                expandOnClick={expandOnClick}
                onSelect={handleItemClick}
                onToggleSubmenu={handleToggleSubmenu}
                onCloseAllSubmenus={handleCloseAllSubmenus}
                showCheckbox={
                  item.showCheckbox === false
                    ? false
                    : showCheckboxes || item.showCheckbox
                }
                toolTip={item.toolTip ?? toolTip}
                containerRef={menuRef}
              />
              {item.divider && index < filteredItems.length - 1 && (
                <div
                  className="border-t"
                  style={{
                    borderColor: '#DCDDE0',
                    marginLeft: '8px',
                    marginRight: '8px',
                  }}
                  role="separator"
                />
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
};

export default Menu;
